import type { AgenticSettings, AgenticUpdateItem, AgenticUpdateKind } from "../api/agenticClient";
import { updateAgenticSettings } from "../api/agenticClient";
import { AGENTIC_SETTINGS_CHANGED_EVENT } from "./agenticRefreshSettings";
import { collapsibleOpenAttr } from "./collapsibleSections";
import type { WheelLedgerTransaction } from "./wheelActions";
import { historyActionLabel, renderHistoryActionPill } from "./historyLabels";
import {
  emptyLedgerCatalog,
  renderRobinhoodImportTargetHtml,
  resolveImportTargetSelection,
  type LedgerCatalogState,
} from "./ledgerBrokersSettings";
import { saveOwnLedgerImportSettings } from "./ownLedgerClient";
import { accountShortCellHtml, brokerMarkCellHtml, markForAccountId } from "./ledgerMarks";

const IMPORT_COLLAPSIBLE_SCOPE = "import";
const UPDATE_KIND_ORDER: AgenticUpdateKind[] = ["sto", "btc", "exp", "asgn"];

interface ImportDayBucket {
  date: string;
  updates: AgenticUpdateItem[];
  pendingCount: number;
  kindCounts: Partial<Record<AgenticUpdateKind, number>>;
}

function updateDayKey(update: AgenticUpdateItem): string {
  const iso = update.receivedAt ?? update.detectedAt;
  if (!iso) return "unknown";
  try {
    const date = new Date(iso);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch {
    return iso.slice(0, 10);
  }
}

function groupUpdatesByDay(updates: AgenticUpdateItem[]): ImportDayBucket[] {
  const byDate = new Map<string, AgenticUpdateItem[]>();
  for (const update of updates) {
    const key = updateDayKey(update);
    const list = byDate.get(key) ?? [];
    list.push(update);
    byDate.set(key, list);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, dayUpdates]) => {
      const sorted = [...dayUpdates].sort((a, b) => {
        const ta = Date.parse(a.receivedAt ?? a.detectedAt);
        const tb = Date.parse(b.receivedAt ?? b.detectedAt);
        return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
      });
      const kindCounts: Partial<Record<AgenticUpdateKind, number>> = {};
      let pendingCount = 0;
      for (const update of sorted) {
        kindCounts[update.kind] = (kindCounts[update.kind] ?? 0) + 1;
        if (update.status === "pending") pendingCount += 1;
      }
      return { date, updates: sorted, pendingCount, kindCounts };
    });
}

function renderDaySummaryChips(bucket: ImportDayBucket): string {
  const parts: string[] = [];
  if (bucket.pendingCount) parts.push(`${bucket.pendingCount} pending`);
  for (const kind of UPDATE_KIND_ORDER) {
    const count = bucket.kindCounts[kind];
    if (count) parts.push(`${count} ${updateKindLabel(kind)}`);
  }
  return parts.join(" · ");
}

function renderDayTotalLabel(bucket: ImportDayBucket): string {
  const n = bucket.updates.length;
  return `${n} ${n === 1 ? "change" : "changes"}`;
}

export function updateKindLabel(kind: AgenticUpdateKind): string {
  switch (kind) {
    case "sto":
      return "STO";
    case "btc":
      return "BTC";
    case "exp":
      return "EXP";
    case "asgn":
      return "ASGN";
    default:
      return kind.toUpperCase();
  }
}

function formatTimeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatImportMetaText(opts: {
  settings: AgenticSettings;
  snapshotRefreshedAt: string | null;
  nextAutoRefreshSec: number | null;
  loading?: boolean;
}): string {
  return importMetaText(opts);
}

const IMPORT_LOADING_META = "Loading sync status…";

function importMetaText(opts: {
  settings: AgenticSettings;
  snapshotRefreshedAt: string | null;
  nextAutoRefreshSec: number | null;
  loading?: boolean;
}): string {
  // Sync timers read as facts, so show nothing rather than "never" / "sync is off"
  // guessed from defaults while the real status is still loading.
  if (opts.loading) return IMPORT_LOADING_META;
  const refreshEnabled = opts.settings.snapshotAutoRefreshEnabled;
  const nextText =
    !refreshEnabled
      ? "Sync is off"
      : opts.nextAutoRefreshSec === 0
        ? "Next sync due now"
        : opts.nextAutoRefreshSec != null
          ? `Next sync in ${Math.max(1, Math.ceil(opts.nextAutoRefreshSec / 60))} min`
          : "Connect Robinhood to enable sync";
  return `Last sync: ${formatTimeAgo(opts.snapshotRefreshedAt)} · ${nextText}`;
}

function importSettingsHtml(opts: {
  settings: AgenticSettings;
  snapshotRefreshedAt: string | null;
  nextAutoRefreshSec: number | null;
  connected: boolean;
  snapshotRefreshing: boolean;
  loading?: boolean;
  catalog?: LedgerCatalogState;
  selectedBrokerId?: string;
  selectedAccountId?: string;
}): string {
  if (opts.loading) {
    // Same wrapper and row height as the real controls, so nothing shifts on arrival.
    return `
    <div class="sfe-agentic-import-settings" aria-busy="true">
      <div class="sfe-agentic-import-controls">
        <span class="muted small">Loading sync settings…</span>
      </div>
    </div>`;
  }
  const { settings } = opts;
  const refreshEnabled = settings.snapshotAutoRefreshEnabled;
  const refreshButton = `<button type="button" class="small sfe-agentic-import-refresh" data-agentic-action="snapshot-refresh"${!opts.connected || opts.snapshotRefreshing ? " disabled" : ""}${!opts.connected ? ' title="Connect Robinhood to sync"' : ""}>Sync Now</button>`;
  return `
    <div class="sfe-agentic-import-settings">
      <div class="sfe-agentic-import-controls">
        <label class="sfe-agentic-import-schedule-toggle">
          <input type="checkbox" data-agentic-import-schedule-enabled${refreshEnabled ? " checked" : ""} />
          <span>Enable Sync</span>
        </label>
        <label class="sfe-agentic-import-interval">
          <span class="small">Sync every</span>
          <input
            type="number"
            min="1"
            max="120"
            step="1"
            data-agentic-import-minutes
            value="${settings.snapshotAutoRefreshMinutes}"
          />
          <span class="small">min</span>
        </label>
        ${refreshButton}
        <label class="sfe-agentic-import-toggle sfe-agentic-import-auto">
          <input type="checkbox" data-agentic-auto-import${settings.autoImportEnabled ? " checked" : ""} />
          <span>Auto import transactions<span class="hint" tabindex="0" role="img" data-tip="When auto import is on, new Robinhood changes are added to your ledger automatically after each sync (manual or scheduled). Existing ledger entries are linked instead of duplicated.">i</span></span>
        </label>
      </div>
      ${
        opts.catalog
          ? renderRobinhoodImportTargetHtml(
              opts.catalog,
              opts.selectedBrokerId ?? "",
              opts.selectedAccountId ?? "",
            )
          : ""
      }
    </div>`;
}

function updateKindHint(kind: AgenticUpdateKind, settlementPending = false): string {
  switch (kind) {
    case "sto":
      return "New short option opened in Robinhood";
    case "btc":
      return "Option closed (buy to close)";
    case "exp":
      return settlementPending
        ? "Option expired (pending settlement — replaces with settled txn on Monday sync)"
        : "Option expired";
    case "asgn":
      return settlementPending
        ? "Option assigned (pending settlement — replaces with settled txn on Monday sync)"
        : "Option assigned";
    default:
      return "Snapshot change";
  }
}

function formatReceivedAt(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function fmtMoney(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

function renderLedgerEmptyState(update: AgenticUpdateItem): string {
  if (update.status === "pending") {
    return `<p class="muted small">No linked ledger transactions yet.</p>`;
  }
  return `<p class="muted small">No matching ledger transaction found for this import.</p>`;
}

function renderLedgerTxnRows(
  txns: WheelLedgerTransaction[],
  update: AgenticUpdateItem,
  hydrating: boolean,
  accountId: string,
): string {
  if (hydrating && !txns.length) {
    return `<p class="muted small">Loading ledger transactions…</p>`;
  }
  if (!txns.length) {
    return renderLedgerEmptyState(update);
  }
  const rows = txns
    .map((txn) => {
      const label = historyActionLabel(txn);
      const markCells = (() => {
        const mark = markForAccountId(accountId);
        return `${brokerMarkCellHtml(mark)}${accountShortCellHtml(mark)}`;
      })();
      return `
        <tr>
          <td class="sfe-ledger-col-id">#${esc(String(txn.id))}</td>
          ${markCells}
          <td class="sfe-ledger-col-action">${renderHistoryActionPill(txn)}</td>
          <td class="sfe-ledger-col-date">${esc(txn.trade_date ?? "—")}</td>
          <td class="sfe-ledger-col-qty num">${esc(String(txn.quantity ?? "—"))}</td>
          <td class="sfe-ledger-col-price num">${esc(fmtMoney(txn.avg_price))}</td>
          <td class="sfe-ledger-col-code">${esc(label)}</td>
        </tr>`;
    })
    .join("");
  return `
    <table class="sfe-agentic-updates-ledger-table">
      <caption class="sfe-agentic-ledger-caption">Ledger transaction${txns.length === 1 ? "" : "s"}</caption>
      <thead>
        <tr>
          <th scope="col" class="sfe-ledger-col-id">ID</th>
          <th scope="col" class="sfe-mark-head" title="Broker">Br</th>
          <th scope="col" class="sfe-mark-head sfe-acct-col" title="Account">Ac</th>
          <th scope="col" class="sfe-ledger-col-action">Action</th>
          <th scope="col" class="sfe-ledger-col-date">Date</th>
          <th scope="col" class="sfe-ledger-col-qty num">Qty</th>
          <th scope="col" class="sfe-ledger-col-price num" title="Premium or price per share">Price</th>
          <th scope="col" class="sfe-ledger-col-code">Code</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function renderUpdateRows(
  update: AgenticUpdateItem,
  expanded: boolean,
  addFormOpen: boolean,
  ledgerTxns: WheelLedgerTransaction[],
  defaultTradeDate: string,
  defaultAvgPrice: string,
  busy: boolean,
  ledgerHydrating: boolean,
  accountId: string,
): string {
  const statusLabel =
    update.status === "added"
      ? update.importSource === "auto"
        ? "Imported"
        : "Added"
      : update.status === "manual"
        ? "Manual"
        : "Pending";
  const statusClass =
    update.status === "added"
      ? "added"
      : update.status === "manual"
        ? "manual"
        : "pending";
  const showAddForm = addFormOpen && update.status === "pending";
  const needsPrice = update.kind === "sto" || update.kind === "btc";
  const priceLabel = update.kind === "sto" ? "Premium" : "BTC premium";
  const receivedIso = update.receivedAt ?? update.detectedAt;
  const receivedTitle = formatReceivedAt(receivedIso);
  const hint = updateKindHint(update.kind, update.settlementPending === true);
  const kindLabel = updateKindLabel(update.kind);

  const mainRow = `
    <tr class="sfe-agentic-update-row sfe-agentic-update-row-${statusClass}" data-agentic-update-id="${esc(update.id)}">
      <td class="sfe-agentic-update-expand-cell">
        <button
          type="button"
          class="sfe-agentic-update-expand"
          data-agentic-update-action="toggle"
          data-agentic-update-id="${esc(update.id)}"
          aria-expanded="${expanded ? "true" : "false"}"
          title="${expanded ? "Hide ledger details" : "Show ledger details"}"
        >${expanded ? "▾" : "▸"}</button>
      </td>
      <td>
        <span class="sfe-agentic-update-kind sfe-agentic-update-kind-${esc(update.kind)}" title="${esc(hint)}">${esc(kindLabel)}</span>
      </td>
      <td class="sfe-agentic-update-contract">${esc(update.label)}</td>
      ${brokerMarkCellHtml(markForAccountId(accountId))}
      ${accountShortCellHtml(markForAccountId(accountId))}
      <td class="sfe-agentic-update-received" title="${esc(receivedTitle)}">${esc(receivedTitle)}</td>
      <td>
        <span class="sfe-agentic-update-status sfe-agentic-update-status-${statusClass}">${esc(statusLabel)}</span>
      </td>
      <td class="sfe-agentic-update-actions">
        <div class="row">
          <button
            type="button"
            class="small"
            data-agentic-update-action="add"
            data-agentic-update-id="${esc(update.id)}"
            ${update.status !== "pending" || busy ? " disabled" : ""}
          >${showAddForm ? "Confirm" : "Add"}</button>
          <button
            type="button"
            class="small ghost"
            data-agentic-update-action="manual"
            data-agentic-update-id="${esc(update.id)}"
            ${update.status !== "pending" || busy ? " disabled" : ""}
          >Manual</button>
        </div>
      </td>
    </tr>`;

  const detailRows = expanded
    ? `
    <tr class="sfe-agentic-update-details-row">
      <td colspan="8">
        <div class="sfe-agentic-update-details panel">
          ${renderLedgerTxnRows(ledgerTxns, update, ledgerHydrating, accountId)}
        </div>
      </td>
    </tr>`
    : "";

  const formRow = showAddForm
    ? `
    <tr class="sfe-agentic-update-form-row">
      <td colspan="8">
        <form class="sfe-agentic-update-add-form panel" data-agentic-update-form="${esc(update.id)}">
          <label class="sfe-agentic-field">
            <span class="small muted">Trade date</span>
            <input type="date" data-agentic-update-trade-date value="${esc(defaultTradeDate)}" required />
          </label>
          ${
            needsPrice
              ? `
          <label class="sfe-agentic-field">
            <span class="small muted">${esc(priceLabel)} per share</span>
            <input type="number" min="0" step="0.01" data-agentic-update-avg-price value="${esc(defaultAvgPrice)}" required />
          </label>`
              : ""
          }
        </form>
      </td>
    </tr>`
    : "";

  return mainRow + formRow + detailRows;
}

function renderUpdatesTable(
  updates: AgenticUpdateItem[],
  opts: Pick<
    Parameters<typeof renderUpdatesPanel>[0],
    | "expandedIds"
    | "addFormOpenId"
    | "ledgerByUpdateId"
    | "ledgerHydratingIds"
    | "defaultTradeDate"
    | "defaultAvgPriceFor"
    | "busyUpdateId"
    | "selectedAccountId"
  >,
): string {
  return `
    <table class="sfe-agentic-updates-table sfe-income-txn-table recs">
      <thead>
        <tr>
          <th scope="col" aria-label="Expand"></th>
          <th scope="col">Kind</th>
          <th scope="col">Contract</th>
          <th scope="col" class="sfe-mark-head" title="Broker">Br</th>
          <th scope="col" class="sfe-mark-head sfe-acct-col" title="Account">Ac</th>
          <th scope="col">Received</th>
          <th scope="col">Status</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        ${updates
          .map((update) =>
            renderUpdateRows(
              update,
              opts.expandedIds.has(update.id),
              opts.addFormOpenId === update.id,
              opts.ledgerByUpdateId.get(update.id) ?? [],
              opts.defaultTradeDate,
              opts.defaultAvgPriceFor(update),
              opts.busyUpdateId === update.id,
              opts.ledgerHydratingIds.has(update.id),
              opts.selectedAccountId ?? "",
            ),
          )
          .join("")}
      </tbody>
    </table>`;
}

function renderUpdatesByDay(
  updates: AgenticUpdateItem[],
  opts: Pick<
    Parameters<typeof renderUpdatesPanel>[0],
    | "expandedIds"
    | "addFormOpenId"
    | "ledgerByUpdateId"
    | "ledgerHydratingIds"
    | "defaultTradeDate"
    | "defaultAvgPriceFor"
    | "busyUpdateId"
    | "selectedAccountId"
  >,
): string {
  const days = groupUpdatesByDay(updates);
  return `
    <div class="sfe-income-days sfe-agentic-updates-days">
      ${days
        .map((day) => {
          const chips = renderDaySummaryChips(day);
          const dayId = `day-${day.date}`;
          return `
        <details
          class="collapsible sfe-income-day sfe-agentic-update-day"
          data-sfe-collapsible="${esc(dayId)}"
          data-sfe-collapsible-default="closed"${collapsibleOpenAttr(IMPORT_COLLAPSIBLE_SCOPE, dayId, false)}
        >
          <summary class="sfe-income-day-summary">
            <span class="sfe-income-summary-row">
              <span class="sfe-income-day-date">
                ${esc(day.date)}
                ${chips ? `<span class="sfe-income-day-chips muted small">${esc(chips)}</span>` : ""}
              </span>
              <span class="sfe-income-day-total${day.pendingCount ? " sfe-agentic-day-pending-total" : ""}">${esc(renderDayTotalLabel(day))}</span>
            </span>
          </summary>
          <div class="sfe-income-txn-wrap sfe-agentic-updates-table-wrap">
            ${renderUpdatesTable(day.updates, opts)}
          </div>
        </details>`;
        })
        .join("")}
    </div>`;
}

export function renderUpdatesPanel(opts: {
  updates: AgenticUpdateItem[];
  hasPreviousSnapshot: boolean;
  sectionOpen: boolean;
  expandedIds: Set<string>;
  addFormOpenId: string | null;
  ledgerByUpdateId: Map<string, WheelLedgerTransaction[]>;
  ledgerHydratingIds: Set<string>;
  defaultTradeDate: string;
  defaultAvgPriceFor: (update: AgenticUpdateItem) => string;
  busyUpdateId: string | null;
  pendingCount?: number;
  historyCount?: number;
  settings: AgenticSettings;
  snapshotRefreshedAt: string | null;
  nextAutoRefreshSec: number | null;
  connected: boolean;
  snapshotRefreshing: boolean;
  loading?: boolean;
  catalog?: LedgerCatalogState;
  selectedBrokerId?: string;
  selectedAccountId?: string;
  loadError?: string | null;
}): string {
  const pendingCount = opts.pendingCount ?? opts.updates.filter((item) => item.status === "pending").length;
  const historyCount = opts.historyCount ?? opts.updates.length;
  const summaryHint = opts.loading
    ? "Loading…"
    : opts.loadError
      ? "Import history unavailable"
      : !opts.hasPreviousSnapshot
      ? "Refresh twice to detect changes"
      : historyCount
        ? pendingCount
          ? `${pendingCount} pending · ${historyCount} in history`
          : `${historyCount} in history`
        : "No changes since last refresh";

  const body = opts.loading
    ? `<p class="muted small">Loading Robinhood changes…</p>`
    : opts.loadError
      ? `<p class="sfe-agentic-error" role="alert">${esc(opts.loadError)}</p>`
    : !opts.hasPreviousSnapshot
      ? `<p class="muted small">Robinhood sync runs on your timer in Settings (default every 7 minutes). After two syncs, option sells, buy-to-closes, expirations, and assignments appear here. Filled orders are also detected when a position closes between syncs.</p>`
      : !opts.updates.length
        ? `<p class="muted small">No option lifecycle changes detected yet. New changes are saved to history when found.</p>`
        : renderUpdatesByDay(opts.updates, {
            expandedIds: opts.expandedIds,
            addFormOpenId: opts.addFormOpenId,
            ledgerByUpdateId: opts.ledgerByUpdateId,
            ledgerHydratingIds: opts.ledgerHydratingIds,
            defaultTradeDate: opts.defaultTradeDate,
            defaultAvgPriceFor: opts.defaultAvgPriceFor,
            busyUpdateId: opts.busyUpdateId,
            selectedAccountId: opts.selectedAccountId,
          });

  return `
    <details class="collapsible sfe-agentic-updates-collapsible" data-agentic-updates-section data-sfe-collapsible="updates"${opts.sectionOpen ? " open" : ""}>
      <summary class="sfe-agentic-updates-summary">
        <h2 class="insight-h">Import</h2>
        <span class="muted small sfe-agentic-import-meta sfe-agentic-updates-sync-meta" data-agentic-import-meta role="status">${esc(importMetaText({
          settings: opts.settings,
          snapshotRefreshedAt: opts.snapshotRefreshedAt,
          nextAutoRefreshSec: opts.nextAutoRefreshSec,
          loading: opts.loading,
        }))}</span>
        <span class="muted small sfe-agentic-updates-summary-hint">${esc(summaryHint)}</span>
      </summary>
      <div class="sfe-agentic-updates-panel">
        ${importSettingsHtml({
          settings: opts.settings,
          snapshotRefreshedAt: opts.snapshotRefreshedAt,
          nextAutoRefreshSec: opts.nextAutoRefreshSec,
          connected: opts.connected,
          snapshotRefreshing: opts.snapshotRefreshing,
          loading: opts.loading,
          catalog: opts.catalog,
          selectedBrokerId: opts.selectedBrokerId,
          selectedAccountId: opts.selectedAccountId,
        })}
        ${body}
      </div>
    </details>`;
}

export function agenticUpdateToLedgerInput(update: AgenticUpdateItem) {
  return {
    kind: update.kind,
    ticker: update.ticker,
    side: update.side,
    strike: update.strike,
    expDate: update.expDate,
    quantity: update.quantity,
    optionSymbol: update.optionSymbol,
    avgPrice: update.avgPrice,
  };
}

export interface ImportSettingsContext {
  settings: AgenticSettings;
  snapshotRefreshedAt: string | null;
  nextAutoRefreshSec: number | null;
  /** Set while the first sync status is still in flight, so timers stay hidden. */
  loading?: boolean;
  catalog?: LedgerCatalogState;
  selectedBrokerId?: string;
  selectedAccountId?: string;
}

function readImportSettingsInputs(host: ParentNode): Partial<AgenticSettings> {
  const autoImportEnabled =
    host.querySelector<HTMLInputElement>("[data-agentic-auto-import]")?.checked ?? false;
  const snapshotAutoRefreshEnabled =
    host.querySelector<HTMLInputElement>("[data-agentic-import-schedule-enabled]")?.checked ?? true;
  const minutesRaw = Number(
    host.querySelector<HTMLInputElement>("[data-agentic-import-minutes]")?.value ?? "7",
  );
  const snapshotAutoRefreshMinutes = Number.isFinite(minutesRaw)
    ? Math.min(120, Math.max(1, Math.round(minutesRaw)))
    : 7;
  return {
    autoImportEnabled,
    snapshotAutoRefreshEnabled,
    snapshotAutoRefreshMinutes,
  };
}

export function bindImportSettings(
  host: ParentNode,
  ctxRef: { current: ImportSettingsContext },
  options: {
    onSaved?: (settings: AgenticSettings) => void;
    onError?: (message: string) => void;
    onTargetChanged?: (selection: { brokerId: string; accountId: string }) => void;
  } = {},
): () => void {
  let saveTimer: ReturnType<typeof setTimeout> | null = null;

  const updateMeta = (): void => {
    const el = host.querySelector<HTMLElement>("[data-agentic-import-meta]");
    if (el) el.textContent = importMetaText(ctxRef.current);
  };

  const save = async (): Promise<void> => {
    const inputs = readImportSettingsInputs(host);
    try {
      const settings = await updateAgenticSettings(inputs);
      ctxRef.current = {
        ...ctxRef.current,
        settings: {
          ...ctxRef.current.settings,
          ...settings,
        },
      };
      window.dispatchEvent(new CustomEvent(AGENTIC_SETTINGS_CHANGED_EVENT));
      options.onSaved?.(settings);
      updateMeta();
    } catch (err) {
      options.onError?.(err instanceof Error ? err.message : "Failed to save import settings");
    }
  };

  const queueSave = (): void => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void save();
    }, 400);
  };

  const saveTarget = async (nextBrokerId?: string): Promise<void> => {
    const catalog = ctxRef.current.catalog ?? emptyLedgerCatalog();
    const brokerSelect = host.querySelector<HTMLSelectElement>("[data-rh-import-broker]");
    const accountSelect = host.querySelector<HTMLSelectElement>("[data-rh-import-account]");
    const selection = resolveImportTargetSelection(
      catalog,
      nextBrokerId ?? brokerSelect?.value ?? ctxRef.current.selectedBrokerId,
      nextBrokerId && nextBrokerId !== ctxRef.current.selectedBrokerId
        ? ""
        : accountSelect?.value ?? ctxRef.current.selectedAccountId,
    );
    ctxRef.current = {
      ...ctxRef.current,
      selectedBrokerId: selection.brokerId,
      selectedAccountId: selection.accountId,
    };
    if (brokerSelect) brokerSelect.value = selection.brokerId;
    if (accountSelect && nextBrokerId) {
      const target = host.querySelector<HTMLElement>(".sfe-agentic-import-target");
      if (target) {
        target.outerHTML = renderRobinhoodImportTargetHtml(
          catalog,
          selection.brokerId,
          selection.accountId,
        );
        bindTargetSelects();
      }
    } else if (accountSelect) {
      accountSelect.value = selection.accountId;
    }
    try {
      await saveOwnLedgerImportSettings({
        defaultRobinhoodBrokerId: selection.brokerId,
        defaultRobinhoodAccountId: selection.accountId,
      });
      options.onTargetChanged?.(selection);
    } catch (err) {
      options.onError?.(err instanceof Error ? err.message : "Failed to save import account");
    }
  };

  const bindTargetSelects = (): void => {
    host.querySelector<HTMLSelectElement>("[data-rh-import-broker]")?.addEventListener("change", (event) => {
      const select = event.currentTarget as HTMLSelectElement;
      void saveTarget(select.value);
    });
    host.querySelector<HTMLSelectElement>("[data-rh-import-account]")?.addEventListener("change", () => {
      void saveTarget();
    });
  };

  host.querySelector<HTMLInputElement>("[data-agentic-auto-import]")?.addEventListener("change", queueSave);
  host.querySelector<HTMLInputElement>("[data-agentic-import-minutes]")?.addEventListener("change", queueSave);
  host
    .querySelector<HTMLInputElement>("[data-agentic-import-schedule-enabled]")
    ?.addEventListener("change", queueSave);
  bindTargetSelects();

  const metaTimer = window.setInterval(() => {
    updateMeta();
  }, 30_000);

  return () => {
    if (saveTimer) clearTimeout(saveTimer);
    window.clearInterval(metaTimer);
  };
}
